import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useControllerTab } from "./use-controller-tab";
import { useParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { getStructuresById, TournamentResponse } from "../../../api/tournament";
import { pubState } from "../../../recoil/pub.atom";
import { TimerInfoState } from "../../timer-controller";
import { calculateTimes } from "./timer";
import { useTimer } from "./use-timer";
import { StructuresResponse } from "../../tournament/hook/useEditTournament";
import { getQrMember, QrMemberResponse } from "../../../api/member";
import { isRegistrationAvailable } from "../../../utils/timer";
import { dialogState } from "../../../recoil/dialog.atom";
import { decryptData, secretKey } from "../../../utils/qr-code";
import { updateTotalStackCount } from "../../../api/tournament-timer";
import { isTournamentAccess } from "../../../utils/owner";

interface ControllerContextType extends ReturnType<typeof useControllerTab> {
  getData: () => void;
  updateTotalStack: (v: number) => void;
  data: TournamentResponse | null;
  timeInfo: TimerInfoState;
  level: number;
  notice: string;
  setNotice: (v: string) => void;
  countdownActive: boolean;
  setCountdownActive: (v: boolean) => void;
  upSound: number | null;
  setUpSound: (v: number | null) => void;
  diffTime: number | null;
  setDiffTime: (v: number | null) => void;
  isCountDown: boolean;
  playSound: (v: number) => void;
  qrCode: string;
  setQrCode: (v: string) => void;
  isPopup: boolean;
  setIsPopup: (v: boolean) => void;
  isMemberPopup: boolean;
  setIsMemberPopup: (v: boolean) => void;
  qrUserInfo: QrMemberResponse | null;
  setQrUserInfo: (v: QrMemberResponse | null) => void;
  isTtsPlaying: boolean;
  setIsTtsPlaying: (v: boolean) => void;
}

const ControllerContext = createContext<ControllerContextType | null>(null);

export const ControllerProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  // 탭
  const { controllerTab, setControllerTab, isControllerTab } =
    useControllerTab();
  const { id } = useParams();
  const tournamentId = id?.replace(/[^0-9]/g, "");
  const pub = useRecoilValue(pubState);
  const setDialog = useSetRecoilState(dialogState);
  // 토너먼트 데이터
  const { connectSocket, data, setData, canUsed } = useTimer(
    tournamentId,
    pub?.id,
  );
  const [timeInfo, setTimeInfo] = useState<TimerInfoState>({
    elapsedTimeMs: null as number | null,
    elapsedTime: "00:00:00" as string | null,
    currentLevel: null as StructuresResponse | null,
    remainingTimeInCurrentLevel: "00:00",
    timeUntilNextBreak: "00:00" as string | null,
    elapsedTimeInCurrentLevel: "00분00초" as string | null,
    elapsedTimeInCurrentLevelMs: null as number | null,
    remainingTimeInCurrentLevelMs: null as number | null,
  });
  // 현재 레벨
  const [level, setLevel] = useState<number>(0);
  // 공지사항
  const [notice, setNotice] = useState("");
  // 팝업 상태
  const [isPopup, setIsPopup] = useState(false);
  const [isMemberPopup, setIsMemberPopup] = useState(false);
  // 사운드
  const [countdownActive, setCountdownActive] = useState<boolean>(true);
  const [upSound, setUpSound] = useState<number | null>(0);
  const [diffTime, setDiffTime] = useState<null | number>(null);
  const [isTtsPlaying, setIsTtsPlaying] = useState(false);
  let isCountDown = false;
  // QR code
  const [qrCode, setQrCode] = useState("");
  const [qrUserInfo, setQrUserInfo] = useState<QrMemberResponse | null>(null);
  // TTS 재생 여부 추적 (중복 재생 방지)
  const ttsPlayedRef = useRef<{ level: number | null; type: string | null }>({
    level: null,
    type: null,
  });

  useEffect(() => {
    const countdownSound = localStorage.getItem("countdown-sound");
    if (!countdownSound) {
      localStorage.setItem("countdown-sound", "true");
      setCountdownActive(true);
      return;
    }
    const isCountdownActive = countdownSound === "true";
    setCountdownActive(isCountdownActive);

    const ttsSound = localStorage.getItem("tts-sound");
    if (!ttsSound) {
      localStorage.setItem("tts-sound", "false");
      setIsTtsPlaying(false);
      return;
    }
    const isTtsPlaying = ttsSound === "true";
    setIsTtsPlaying(isTtsPlaying);

    const blindUpSound = localStorage.getItem("blind-up-sound");
    if (!blindUpSound) {
      localStorage.setItem("blind-up-sound", "0");
      setUpSound(0);
      return;
    }

    setUpSound(blindUpSound === "null" ? null : +blindUpSound);
  }, []);

  // QR 읽기
  useEffect(() => {
    let buffer = "";
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isPopup) return;

      if (e.key === '"' && !buffer) {
        buffer = "";
        return;
      }

      if (e.key === '"' && buffer) {
        setQrCode(buffer);
        buffer = "";
        return;
      }

      if (e.key?.length === 1) {
        buffer += e.key;
      }
    };

    setQrCode(buffer);

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPopup]);

  // qr 코드
  useEffect(() => {
    searchQrMember();
  }, [qrCode]);

  const searchQrMember = async () => {
    if (!data || !pub || !qrCode) return;
    const isRegistration = isRegistrationAvailable(data);
    if (!isRegistration) {
      setDialog({
        title: "레지마감되었습니다. ",
        isCancel: false,
        confirm: () => {
          setDialog(null);
        },
      });
      return;
    }

    const qr = await decryptData(qrCode, secretKey);
    const userId = qr.userId;
    const expire = qr.expire;
    const current = new Date().getTime();
    if (!userId || !expire) return;

    if (+expire < +current) {
      alert("만료된 QR입니다. ");
      return;
    }
    try {
      const qrInfo = await getQrMember(userId, pub?.id, data.id);
      setQrCode("");
      setQrUserInfo(qrInfo);
    } catch (e: any) {
      setQrCode("");
      setQrUserInfo(null);
      alert(e.message);
    }
  };

  useEffect(() => {
    connectSocket();
  }, [tournamentId]);

  // 최초 설정
  const getData = async () => {
    if (!tournamentId) {
      return;
    }

    const data = await getStructuresById(+tournamentId);
    setData(data);
  };

  useEffect(() => {
    getData();
  }, []);

  const playSound = (sound: number) => {
    let file = "";
    switch (sound) {
      case 0:
        file = "/sound/blind-up-1.mp3";
        break;
      case 1:
        file = "/sound/blind-up-2.mp3";
        break;
      case 2:
        file = "/sound/level-up-1.mp3";
        break;
      case 3:
        file = "/sound/level-up-2.mp3";
        break;
    }
    const audio = new Audio(file);
    audio.play();
  };

  // TTS 재생 함수
  const playTTS = (text: string, isKorean: boolean) => {
    if (!("speechSynthesis" in window)) {
      console.log("TTS를 지원하지 않는 브라우저입니다.");
      return;
    }

    // 기존 TTS 중지
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);

    // 보이스 선택 함수
    const selectVoice = () => {
      const voices = window.speechSynthesis.getVoices();

      if (voices.length === 0) {
        // 보이스가 아직 로드되지 않은 경우, 이벤트 리스너로 대기
        window.speechSynthesis.onvoiceschanged = () => {
          const loadedVoices = window.speechSynthesis.getVoices();
          setVoiceForUtterance(loadedVoices);
        };
        return;
      }

      setVoiceForUtterance(voices);
    };

    const setVoiceForUtterance = (voices: SpeechSynthesisVoice[]) => {
      const targetVoice = voices.find((voice) => {
        if (isKorean) {
          return voice.name.includes("유나");
        } else {
          return voice.name.includes("Google UK English Male");
        }
      });

      if (targetVoice) {
        utterance.voice = targetVoice;
      } else {
        // 대체 보이스 선택
        const fallbackVoice = voices.find((voice) => {
          if (isKorean) {
            return voice.lang.startsWith("ko");
          } else {
            return voice.lang.startsWith("en-GB");
          }
        });
        if (fallbackVoice) {
          utterance.voice = fallbackVoice;
        }
      }

      utterance.lang = isKorean ? "ko-KR" : "en-GB";
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      window.speechSynthesis.speak(utterance);
    };

    selectVoice();
  };

  const setSocketData = async () => {
    if (!tournamentId || !data) {
      return;
    }
    if (data.timerDto && data.timerDto.now && !diffTime) {
      const now = new Date();
      const timeDiff = now.getTime() - new Date(data.timerDto.now).getTime();

      setDiffTime(timeDiff);
    }
    setNotice(data.notice ? data.notice : "");
  };

  useEffect(() => {
    setSocketData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!data || !data.timerDto) {
        return;
      }
      const updatedTimeInfo = calculateTimes(
        data?.timerDto,
        data?.structure,
        diffTime,
      );
      setTimeInfo(updatedTimeInfo);
      let nextLevel: StructuresResponse | null = null;
      if (updatedTimeInfo.currentLevel?.ord) {
        nextLevel = data?.structure[updatedTimeInfo.currentLevel?.ord];
      }
      if (
        updatedTimeInfo.remainingTimeInCurrentLevel === "00:03" &&
        isCountDown === false &&
        countdownActive &&
        nextLevel &&
        data.timerDto &&
        data.timerDto.isStop === false
      ) {
        const audio = new Audio("/sound/count-down.mp3");
        audio.play();
        isCountDown = true;
        if (
          nextLevel.type === "LEVEL" &&
          updatedTimeInfo.currentLevel?.type === "LEVEL"
        ) {
          setTimeout(() => {
            if (upSound !== null) {
              playSound(upSound);
            }
            isCountDown = false;
          }, 4000);
        }
        setTimeout(() => {
          isCountDown = false;
        }, 1000);
      }
      if (
        !countdownActive &&
        updatedTimeInfo.remainingTimeInCurrentLevel === "00:00" &&
        nextLevel &&
        nextLevel.type === "LEVEL" &&
        updatedTimeInfo.currentLevel?.type === "LEVEL" &&
        data.timerDto &&
        data.timerDto.isStop === false
      ) {
        if (upSound !== null) {
          playSound(upSound);
        }
      }

      //현재 레벨 00:01일 때 현재 레벨 설명 TTS 재생
      if (
        updatedTimeInfo.elapsedTimeInCurrentLevel === "00분01초" &&
        updatedTimeInfo.currentLevel &&
        data.timerDto &&
        data.timerDto.isStop === false &&
        isTtsPlaying
      ) {
        const currentLevel = updatedTimeInfo.currentLevel;

        // 중복 재생 방지
        if (
          ttsPlayedRef.current.level !== currentLevel.ord ||
          ttsPlayedRef.current.type !== currentLevel.type
        ) {
          ttsPlayedRef.current = {
            level: currentLevel.ord,
            type: currentLevel.type,
          };

          if (currentLevel.type === "LEVEL" && currentLevel.level) {
            // LEVEL일 때: "레벨 {level}, small blind {smallBlind}, big blind {bigBlind} {ante ? "앤티 {ante}" : ""}"
            const koreanText = `레벨 ${currentLevel.level}, 스몰 블라인드 ${
              currentLevel.smallBlind?.toLocaleString() || 0
            }, 빅 블라인드 ${currentLevel.bigBlind?.toLocaleString() || 0} ${
              currentLevel.ante
                ? `앤티 ${currentLevel.ante?.toLocaleString() || 0}`
                : ""
            }`;

            // 한글 TTS 재생
            playTTS(koreanText, true);
          } else if (currentLevel.type === "BREAK") {
            // BREAK일 때: "브레이크 타임"
            const koreanText = "브레이크 타임";

            // 한글 TTS 재생
            playTTS(koreanText, true);
          }
        }
      } else if (
        updatedTimeInfo.elapsedTimeInCurrentLevel !== "00분01초" &&
        updatedTimeInfo.currentLevel &&
        isTtsPlaying
      ) {
        // 00:01이 아니면 재생 플래그 리셋
        const currentLevel = updatedTimeInfo.currentLevel;
        if (
          ttsPlayedRef.current.level !== currentLevel.ord ||
          ttsPlayedRef.current.type !== currentLevel.type
        ) {
          ttsPlayedRef.current = {
            level: null,
            type: null,
          };
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [data]);

  useEffect(() => {
    setSocketData();
  }, [data]);

  // 현재 level 설정
  useEffect(() => {
    // 토너먼트 시작 후
    if (timeInfo.currentLevel?.level !== undefined) {
      if (timeInfo.currentLevel?.level !== null) {
        setLevel(timeInfo.currentLevel.level);
        return;
      } else {
        // 브레이크 레벨 -> 이전 레벨로 설정
        const prevLevel = data?.structure[timeInfo.currentLevel?.ord - 2];
        if (prevLevel && prevLevel.level) {
          setLevel(prevLevel.level);
        }
        return;
      }
    }

    // 마지막 레벨 종료 후
    if (timeInfo.currentLevel === null && data?.timerDto) {
      setLevel(-1);
      return;
    }
  }, [timeInfo.currentLevel, data?.timerDto]);

  useEffect(() => {
    if (!canUsed) {
      alert("다른 매장의 토너먼트에 접근할 수 없습니다. ");
      window.close();
    }
  }, [canUsed]);

  // 토탈 스택 조정
  const updateTotalStack = async (value: number) => {
    if (!data) return;
    if (!isTournamentAccess(data.id, pub?.tournamentIds)) {
      alert(`토너먼트 ID ${data.id}에 대한 권한이 없습니다. `);
      return;
    }
    try {
      await updateTotalStackCount(data?.id, value);
      getData();
    } catch (e: any) {
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
    }
  };

  const value = {
    // 탭
    controllerTab,
    setControllerTab,
    isControllerTab,
    // 데이터
    getData,
    data,
    timeInfo,
    level,
    // 공지사항
    notice,
    setNotice,
    // 사운드
    countdownActive,
    setCountdownActive,
    upSound,
    setUpSound,
    diffTime,
    setDiffTime,
    isCountDown,
    playSound,
    // QR
    qrCode,
    setQrCode,
    qrUserInfo,
    setQrUserInfo,
    // 팝업 상태
    isPopup,
    setIsPopup,
    isMemberPopup,
    setIsMemberPopup,
    // 핸들러
    updateTotalStack,
    // TTS
    isTtsPlaying,
    setIsTtsPlaying,
  };

  return (
    <ControllerContext.Provider value={value}>
      {children}
    </ControllerContext.Provider>
  );
};

export const useControllerContext = () => {
  const context = useContext(ControllerContext);
  if (!context) {
    throw new Error("useContext must be used within a GameProvider");
  }
  return context;
};
